import { parseFlaggedLinkUrl, parseLinkUrl } from "./openAppFlag";

const ANDROID_PATTERN = /android/i;

// Instagram, Threads (its user agent says "Barcelona"), Facebook, Messenger.
// Enough on its own to mean Meta's WebView: Chrome and Custom Tabs never carry
// these tokens. Not paired with the WebView's "; wv" token, which Facebook
// strips from its user agent (seen on a device on 2026-10-14, Facebook
// 582.0.0).
const META_IN_APP_BROWSER_PATTERN = /instagram|barcelona|fban|fbav|fb_iab/i;

// The Facebook app itself, not Messenger.
const FACEBOOK_APP_PATTERN = /fb_iab\/fb4a/i;

// Same rule as the Android config form. It also blocks ";", which would let a
// bad package name add its own fields to the intent.
const PACKAGE_NAME_PATTERN =
  /^[a-zA-Z][a-zA-Z0-9_]*(\.[a-zA-Z][a-zA-Z0-9_]*)+$/;

/**
 * True where the page tries to open the app on every visit, flag or not. Known
 * from the request alone, so the server can leave out a screen that would only
 * flash before the attempt replaces it.
 */
export function triesAppOpenOnEveryVisit(
  userAgent: string,
  packageName: string | null | undefined
): boolean {
  return (
    ANDROID_PATTERN.test(userAgent) &&
    FACEBOOK_APP_PATTERN.test(userAgent) &&
    !!packageName &&
    PACKAGE_NAME_PATTERN.test(packageName)
  );
}

/**
 * Workaround for Meta's in-app browsers on Android. They open every link in
 * their own WebView, so App Links never open the installed app. An intent: URL
 * started by the page does open it, after Meta's "leaving the app" prompt.
 *
 * Meta only: other WebViews show an ERR_UNKNOWN_URL_SCHEME error page instead.
 * Tested on a device: Instagram on 2026-10-01, Threads on 2026-10-08,
 * Facebook on 2026-10-14 (also with no tap).
 *
 * Messenger needs the flag here, on Android, but not on iOS (see
 * externalBrowser.ts).
 *
 * The intent and its fallback use the link without the flag. Instagram ignores
 * the fallback. A WebView that follows it runs the normal flow, which records
 * the click and opens the Play Store with the click id. Without a fallback, a
 * missing app could open the Play Store with no click id.
 */
export function buildAppIntentUrl(
  pageUrl: string,
  userAgent: string,
  packageName: string | null | undefined
): string | null {
  if (
    !ANDROID_PATTERN.test(userAgent) ||
    !META_IN_APP_BROWSER_PATTERN.test(userAgent)
  ) {
    return null;
  }
  if (!packageName || !PACKAGE_NAME_PATTERN.test(packageName)) return null;

  // Facebook tries every link, flag or not: with no package below, a visitor
  // without the app still reaches the Play Store, through the browser.
  const triesEveryVisit = triesAppOpenOnEveryVisit(userAgent, packageName);
  const linkUrl = triesEveryVisit
    ? parseLinkUrl(pageUrl)
    : parseFlaggedLinkUrl(pageUrl);
  if (!linkUrl) return null;

  // Facebook ignores the fallback, so with a package a visitor without the app
  // stays on this page. With no package, Android opens the app through its
  // verified App Link, or the default browser when the app is missing, and the
  // normal flow there goes on to the Play Store. Tested on a device on
  // 2026-10-14, both after a tap and with no tap.
  if (triesEveryVisit) {
    return buildIntentUrl(linkUrl, [
      "action=android.intent.action.VIEW",
      "category=android.intent.category.BROWSABLE",
    ]);
  }

  return buildIntentUrl(linkUrl, [
    `package=${packageName}`,
    `S.browser_fallback_url=${encodeURIComponent(linkUrl.href)}`,
  ]);
}

function buildIntentUrl(linkUrl: URL, fields: string[]): string {
  const scheme = linkUrl.protocol.slice(0, -1);
  return (
    `intent://${linkUrl.host}${linkUrl.pathname}${linkUrl.search}` +
    `#Intent;${[`scheme=${scheme}`, ...fields].join(";")};end`
  );
}
